/**
 * Mapper simples de status para o stepper de Acompanhamento.
 *
 * Mantem o contrato minimo necessario para a UI funcionar:
 *   - STATUS_LABELS    : rotulo humano por chave textual
 *   - STATUS_CORES     : cor hex por chave textual
 *   - STATUS_PARA_ETAPA: mapeia a chave na etapa do stepper (1..5)
 *   - TOTAL_ETAPAS     : total fixo em 5 etapas (TCE / TCA / TCA Emp.)
 */

import { PROGRAMA_TCA } from "../utils/tipoPrograma.js";

export const STATUS_LABELS = {
  PENDENTE: "Pendente",
  AGUARDANDO_ESTUDANTE: "Aguardando preenchimento do Estudante",
  AGUARDANDO_VALIDACAO_CIEEMG: "Aguardando validacao do CIEEMG",
  EM_VALIDACAO: "Em validacao pelo CIEEMG",
  AGUARDANDO_CONFECCAO: "Aguardando confeccao do contrato",
  CONTRATO_CONFECCIONADO: "Contrato confeccionado",
  AGUARDANDO_ASSINATURAS: "Aguardando assinaturas",
  DISPONIBILIZADO_PARA_ASSINATURA: "Disponibilizado para assinatura",
  FINALIZADO: "Finalizado",
  APROVADO: "Aprovado",
  REPROVADO_EMPRESA: "Reprovado — voltar p/ Empresa",
  REPROVADO_ESTUDANTE: "Reprovado — voltar p/ Estudante",
  REPROVADO_DEFINITIVO: "Reprovado definitivamente",
  RECUSADO: "Recusado",
};

/**
 * Tipos de programa que exigem etapa extra "Preenchimento do Estudante"
 * entre "Cadastro inicial" e "Validação do preenchimento".
 * Espelha o `PROGRAMA_REQUER_ESTUDANTE` de utils/tipoPrograma.js.
 * Duplicado aqui para evitar import circular (mappers não dependem de utils).
 */
const PROGRAMA_REQUER_ESTUDANTE = new Set([0, 2]);

// Mapeia o status retornado pelo backend para a etapa equivalente no stepper
// quando o programa EXIGE a etapa extra do estudante (programa 0 ou 2).
//   1) Cadastro inicial
//   2) Preenchimento do Estudante
//   3) Validação do preenchimento
//   4) Aguardando assinatura
//   5) Contrato finalizado
export const STATUS_PARA_ETAPA_COM_ESTUDANTE = {
  AGUARDANDO_ESTUDANTE: 2,
  AGUARDANDO_VALIDACAO_CIEEMG: 3,
  PENDENTE: 3,
  EM_VALIDACAO: 3,
  AGUARDANDO_CONFECCAO: 3,
  CONTRATO_CONFECCIONADO: 3,
  AGUARDANDO_ASSINATURAS: 4,
  DISPONIBILIZADO_PARA_ASSINATURA: 4,
  FINALIZADO: 5,
  REPROVADO_EMPRESA: 1,
  REPROVADO_ESTUDANTE: 2,
  REPROVADO_DEFINITIVO: 1,
  APROVADO: 5,
  RECUSADO: 1,
};

// Mapeia o status retornado pelo backend para a etapa equivalente no stepper
// quando o programa NÃO exige etapa extra do estudante (programa 1).
//   1) Cadastro inicial
//   2) Validação do preenchimento
//   3) Aguardando assinatura
//   4) Contrato finalizado
// Mantido como mapa separado para refletir fielmente o fluxo do TCA padrão,
// onde o cadastro da empresa já contempla o preenchimento do estudante.
export const STATUS_PARA_ETAPA_SEM_ESTUDANTE = {
  AGUARDANDO_ESTUDANTE: 1,
  AGUARDANDO_VALIDACAO_CIEEMG: 2,
  PENDENTE: 2,
  EM_VALIDACAO: 2,
  AGUARDANDO_CONFECCAO: 2,
  CONTRATO_CONFECCIONADO: 2,
  AGUARDANDO_ASSINATURAS: 3,
  DISPONIBILIZADO_PARA_ASSINATURA: 3,
  FINALIZADO: 4,
  REPROVADO_EMPRESA: 1,
  REPROVADO_ESTUDANTE: 1,
  REPROVADO_DEFINITIVO: 1,
  APROVADO: 4,
  RECUSADO: 1,
};

// Alias para compatibilidade com consumidores que ainda esperam o nome
// "STATUS_PARA_ETAPA" (sem sufixo). Aponta para o mapa COM estudante, que é
// o que o Acompanhamento sempre utilizou (5 etapas). Quando a regra de
// "Preenchimento do Estudante" por programa for religada na view, basta
// passar `tipoPrograma` para `mapearEtapaPorStatus` e ela escolherá entre
// STATUS_PARA_ETAPA_COM_ESTUDANTE e STATUS_PARA_ETAPA_SEM_ESTUDANTE.
export const STATUS_PARA_ETAPA = STATUS_PARA_ETAPA_COM_ESTUDANTE;

export const STATUS_CORES = {
  PENDENTE: "#3b82f6",
  AGUARDANDO_ESTUDANTE: "#3b82f6",
  AGUARDANDO_VALIDACAO_CIEEMG: "#3b82f6",
  EM_VALIDACAO: "#1e40af",
  AGUARDANDO_CONFECCAO: "#3b82f6",
  CONTRATO_CONFECCIONADO: "#1e40af",
  AGUARDANDO_ASSINATURAS: "#3b82f6",
  DISPONIBILIZADO_PARA_ASSINATURA: "#1e40af",
  FINALIZADO: "#134b8b",
  APROVADO: "#1e3a8a",
  REPROVADO_EMPRESA: "#ef4444",
  REPROVADO_ESTUDANTE: "#ef4444",
  REPROVADO_DEFINITIVO: "#dc2626",
  RECUSADO: "#dc2626",
};

export const TOTAL_ETAPAS = 5;

export function obterStatusChave(status) {
  return String(status || "")
    .trim()
    .toUpperCase()
    .replace(/\s+/g, "_");
}

export function getStatusLabel(status) {
  const chave = obterStatusChave(status);
  if (!chave) return "Sem status";
  return STATUS_LABELS[chave] || chave;
}

export function getStatusCor(status) {
  const chave = obterStatusChave(status);
  return STATUS_CORES[chave] || "#94a3b8";
}

/* ============================================================
   Funções auxiliares para o stepper dinamico baseado no
   `tipo_programa` (0 = TCE / 2 = TCA Empregador → 5 etapas;
   1 = TCA → 4 etapas, sem etapa extra do estudante).
============================================================ */

/** Total padrao de etapas (TCE / TCA Empregador = 5). */
export const TOTAL_ETAPAS_PADRAO = TOTAL_ETAPAS;

/** Status finais que encerram o processo com sucesso. */
const STATUS_FINALIZADOS = new Set(["FINALIZADO", "APROVADO"]);

/** Status que indicam cancelamento/reprovacao. */
const STATUS_REJEITADOS = new Set([
  "REPROVADO_EMPRESA",
  "REPROVADO_ESTUDANTE",
  "REPROVADO_DEFINITIVO",
  "RECUSADO",
]);

/**
 * Mapeia uma chave de status (normalizada) na etapa correspondente
 * do stepper, considerando se o programa exige etapa extra do estudante.
 *
 * Usado pelo `rejeicaoMapper` para resolver a etapa de uma rejeição
 * registrada no `localStorage`.
 */
export function mapearEtapaPorStatus(statusChave, requerSupex = false, tipoPrograma = 1) {
  const chave = obterStatusChave(statusChave);
  if (!chave) return 1;

  let etapa = STATUS_PARA_ETAPA[chave] || 1;

  // Em programas SEM etapa extra (TCA, tipoPrograma === 1) o total
  // de etapas cai de 5 para 4. Status como REPROVADO_ESTUDANTE
  // (etapa 2) não fazem sentido nesse fluxo; mantemos 1.
  const ehProgramaSemEstudante =
    Number(tipoPrograma) === PROGRAMA_TCA || Number(tipoPrograma) === 1;
  if (ehProgramaSemEstudante) {
    if (chave === "AGUARDANDO_ESTUDANTE") etapa = 2;
    if (chave === "REPROVADO_ESTUDANTE") etapa = 1;
  }

  return etapa;
}

/** Total de etapas por programa. */
export function obterTotalEtapasPorPrograma(tipoPrograma) {
  const tp = Number(tipoPrograma);
  // 0 (TCE) e 2 (TCA Empregador) → 5 etapas (com etapa do estudante).
  // 1 (TCA) → 4 etapas (sem etapa do estudante).
  if (tp === 1 || tp === PROGRAMA_TCA) return 4;
  return TOTAL_ETAPAS_PADRAO;
}

/**
 * Resolve o status, etapa, rotulo e cor de um item de acompanhamento.
 * Aplica a regra:
 *   - tipo_programa 0 ou 2 → etapa extra "Preenchimento do Estudante"
 *     entre cadastro inicial e validacao.
 *   - tipo_programa 1 → fluxo sem etapa extra.
 *
 * Prioridade dos dados:
 *   1º) dados estruturados do backend (etapa_atual, label, cor, status_label)
 *      — vêm do endpoint /tca-empregador/:id/status-completo OU da rota
 *      dedicada /acompanhamentos/me (quando a API já implementa derivação);
 *   2º) inferência local via STATUS_PARA_ETAPA (comportamento antigo,
 *      preservado para fallback).
 */
export function normalizarStatusCadastro(
  fonte,
  raw,
  requerSupex,
  etapaRejeicaoHint,
  tipoPrograma,
) {
  // ----- 1) Backend já enviou etapa derivada estruturada -----
  // A nova rota /tca-empregador/acompanhamentos/me devolve cada item
  // com `etapa_atual`, `label`, `cor`, `status_label`, `rejeitado`,
  // `finalizado` e `supex`. Quando esses campos estão presentes, eles
  // são a fonte da verdade — mais confiável que inferir do `status`.
  const baseRaw = raw && Object.keys(raw || {}).length > 0 ? raw : fonte || {};
  const baseFonte = fonte || {};

  const etapaBackend =
    baseRaw?.etapa_atual ?? baseFonte?.etapa_atual ?? null;
  const totalEtapasBackend =
    baseRaw?.total_etapas ?? baseFonte?.total_etapas ?? null;
  const labelBackend =
    baseRaw?.label ?? baseFonte?.label ?? null;
  const corBackend = baseRaw?.cor ?? baseFonte?.cor ?? null;
  const statusLabelBackend =
    baseRaw?.status_label ?? baseFonte?.status_label ?? null;
  const rejeitadoBackend =
    baseRaw?.rejeitado ?? baseFonte?.rejeitado ?? null;
  const finalizadoBackend =
    baseRaw?.finalizado ?? baseFonte?.finalizado ?? null;
  const supexBackend = baseRaw?.supex ?? baseFonte?.supex ?? null;

  if (
    etapaBackend != null &&
    Number.isFinite(Number(etapaBackend))
  ) {
    const totalEtapas =
      Number(totalEtapasBackend) ||
      obterTotalEtapasPorPrograma(tipoPrograma);

    // Mapeia o status_label do backend em uma chave conhecida (best-effort)
    // para reaproveitar a flag `rejeitado` quando o backend não envia.
    const chaveBackend = obterStatusChave(statusLabelBackend || baseRaw?.status || "");
    const ehRejeitado =
      rejeitadoBackend === true || STATUS_REJEITADOS.has(chaveBackend);
    const ehFinalizado =
      finalizadoBackend === true || STATUS_FINALIZADOS.has(chaveBackend);

    return {
      chave: chaveBackend || "PENDENTE",
      statusLabel: labelBackend || statusLabelBackend || chaveBackend || "Pendente",
      statusCor: corBackend || "#3b82f6",
      etapaAtual: Math.min(Number(etapaBackend), totalEtapas),
      totalEtapas,
      finalizado: ehFinalizado,
      rejeitado: ehRejeitado,
      requerSupex: supexBackend === true || Boolean(requerSupex),
    };
  }

  // ----- 2) Fallback: inferência local via STATUS_PARA_ETAPA -----
  const statusBruto =
    fonte?.status || raw?.status || raw?.status_cadastro || "";
  const chave = obterStatusChave(statusBruto);
  if (!chave) return null;

  const totalEtapas = obterTotalEtapasPorPrograma(tipoPrograma);
  let etapaAtual = STATUS_PARA_ETAPA[chave] || 1;

  // Para programas com etapa extra (0 e 2), garante que o status
  // AGUARDANDO_ESTUDANTE seja exibido na etapa 2 e nao pule.
  if (
    (tipoPrograma === 0 || tipoPrograma === 2) &&
    chave === "AGUARDANDO_ESTUDANTE" &&
    etapaAtual > 2
  ) {
    etapaAtual = 2;
  }

  // Ajuste: se o item foi reprovado, manda para a etapa correta
  // (empresa = 1, estudante = 2).
  if (STATUS_REJEITADOS.has(chave)) {
    if (chave === "REPROVADO_ESTUDANTE") etapaAtual = 2;
    else etapaAtual = 1;
  }

  if (typeof etapaRejeicaoHint === "number" && etapaRejeicaoHint > 0) {
    etapaAtual = Math.min(etapaAtual, etapaRejeicaoHint);
  }

  return {
    chave,
    statusLabel: STATUS_LABELS[chave] || chave,
    statusCor: STATUS_CORES[chave] || "#94a3b8",
    etapaAtual,
    totalEtapas,
    finalizado: STATUS_FINALIZADOS.has(chave),
    rejeitado: STATUS_REJEITADOS.has(chave),
    requerSupex: Boolean(requerSupex),
  };
}


